import {
    ComputeStatus,
    DataSourceWithConfigAndEtlExtended,
    DatalabFeature,
    InputDataSourceConfig,
    TargetStatus,
    UpdateDataSourceConfigMutation,
    resource_memory_cost_per_gb_per_hour,
    resource_storage_cost_per_gb_per_hour,
    useFetchOrganizationFreeComputeUnitsRemainingQuery,
    useUpdateDataSourceConfigMutation,
} from "@biggeo/bg-server-lib/studio";
import {
    Button,
    Divider,
    FlexScrollArea,
    FlexScrollAreaContainer,
    Grid,
    IconAvatar,
    ProcessingMeterCard,
    Stack,
    Tooltip,
    Typography,
} from "@biggeo/bg-ui/lab";
import {
    AcUnitOutline,
    ArrowForwardOutline,
    CheckCircle,
} from "@biggeo/bg-ui/lab/icons";
import {
    convertByteSize,
    formatNumberWithPrecision,
    toByteType,
} from "@biggeo/bg-utils";

import { isSuccess } from "@vividtheory/remotedata";
import { useCloseModal } from "../../common/redux/hooks";
import { useErrorToast } from "../../toaster/containers/redux/hooks";
import { useFeatureAccess } from "../../paywall/redux/hooks";

const hasEnoughCredits = ({
    computeFeatureAccess,
    freeComputeUnits,
    credits,
}: {
    computeFeatureAccess: "none" | "unlimited" | number;
    freeComputeUnits: number;
    credits: number;
}) => {
    if (computeFeatureAccess === "none") {
        return freeComputeUnits >= credits;
    }
    if (computeFeatureAccess === "unlimited") {
        return true;
    }
    return computeFeatureAccess + freeComputeUnits >= credits;
};

export const EnableHotComputeContainer = ({
    datasource,
    onConfirm,
}: {
    datasource: DataSourceWithConfigAndEtlExtended;
    onConfirm: (data: UpdateDataSourceConfigMutation) => void;
}) => {
    const closeModal = useCloseModal();
    const errorToast = useErrorToast();

    const {
        executeMutation: updateDataSourceConfig,
        mutationReturn: [_x, { loading: updateLoading }],
    } = useUpdateDataSourceConfigMutation();

    const enableResource = async () => {
        const input: InputDataSourceConfig = {
            id: datasource.configuration.id,
            status: ComputeStatus.loading,
            targetState: TargetStatus.hot,
        };

        return await updateDataSourceConfig({
            variables: {
                input: input,
            },
        });
    };

    const handleConfirm = async () => {
        const value = await enableResource();
        if (!value.data?.updateDataSourceConfig) {
            errorToast("Failed to disable hot compute.");
            return;
        }
        onConfirm(value.data);
        closeModal();
    };

    const storageActual = convertByteSize(
        toByteType(Number(datasource.dataSource.diskActual) || 0, "bytes"),
        "gigabytes"
    );
    const memActual = convertByteSize(
        toByteType(Number(datasource.dataSource.ramActual) || 0, "bytes"),
        "gigabytes"
    );

    const creditsPerHour =
        resource_storage_cost_per_gb_per_hour * storageActual.value +
        resource_memory_cost_per_gb_per_hour * memActual.value;

    const creditsPerDay = creditsPerHour * 24;

    const { featureAccess: computeFeatureAccess } = useFeatureAccess(
        DatalabFeature.datalab_compute_unit_limit
    );

    const { remote: freeComputeUnitsRd } =
        useFetchOrganizationFreeComputeUnitsRemainingQuery();

    const hasCredits = hasEnoughCredits({
        computeFeatureAccess: isSuccess(computeFeatureAccess)
            ? computeFeatureAccess.data.remaining
            : "none",
        freeComputeUnits: isSuccess(freeComputeUnitsRd)
            ? freeComputeUnitsRd.data.fetchOrganizationFreeComputeUnitsRemaining
                  .unitsRemaining
            : 0,
        credits: creditsPerDay,
    });

    const disabled =
        !hasCredits ||
        updateLoading ||
        datasource.configuration.status === ComputeStatus.loading ||
        datasource.configuration.targetState === TargetStatus.hot ||
        datasource.configuration.status === ComputeStatus.hot ||
        creditsPerDay <= 0;

    return (
        <FlexScrollAreaContainer>
            <FlexScrollArea>
                <Stack width="100%" sx={{ padding: 4 }} gap={4}>
                    <Stack
                        width="100%"
                        gap={1}
                        flexDirection="row"
                        alignItems="center"
                    >
                        <IconAvatar color="info">
                            <AcUnitOutline color="info" />
                        </IconAvatar>
                        <ArrowForwardOutline size="md" />
                        <IconAvatar color="success">
                            <CheckCircle color="success" />
                        </IconAvatar>
                    </Stack>
                    <Stack width="100%">
                        <Typography variant="h6" fontWeight="bold">
                            {"Hot Compute Resources"}
                        </Typography>
                        <Typography
                            variant="body2"
                            fontWeight="regular"
                            textColor="disabled.onContainer"
                        >
                            {
                                "See the breakdown below to enable your compute resources usage"
                            }
                        </Typography>
                    </Stack>
                    <Divider />
                    <Stack width="100%" gap={2}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Stack
                                    width="100%"
                                    sx={{
                                        padding: 2,
                                        borderRadius: (theme) =>
                                            theme.radius.default,
                                        border: 1,
                                        borderColor: (theme) =>
                                            theme.palette.stroke[100],
                                    }}
                                >
                                    <Typography
                                        variant="body3"
                                        fontWeight="semibold"
                                    >
                                        {`${formatNumberWithPrecision(creditsPerHour)} credits`}
                                    </Typography>
                                    <Typography
                                        variant="body4"
                                        fontWeight="semibold"
                                        color="disabled"
                                        colorSet="container"
                                        invertColors
                                    >
                                        {"Consumed hourly"}
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Stack
                                    width="100%"
                                    sx={{
                                        padding: 2,
                                        borderRadius: (theme) =>
                                            theme.radius.default,
                                        border: 1,
                                        borderColor: (theme) =>
                                            theme.palette.stroke[100],
                                    }}
                                >
                                    <Typography
                                        variant="body3"
                                        fontWeight="semibold"
                                    >
                                        {`${formatNumberWithPrecision(creditsPerDay)} credits`}
                                    </Typography>
                                    <Typography
                                        variant="body4"
                                        fontWeight="semibold"
                                        color="disabled"
                                        colorSet="container"
                                        invertColors
                                    >
                                        {"24hr Minimum allocation required"}
                                    </Typography>
                                </Stack>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <ProcessingMeterCard
                                type="success"
                                titleSection={{
                                    title: (
                                        <Stack
                                            sx={{
                                                marginBottom: 1,
                                            }}
                                        >
                                            <Typography
                                                variant="body3"
                                                fontWeight="semibold"
                                            >
                                                Current consumption
                                            </Typography>
                                        </Stack>
                                    ),
                                }}
                                stats={{
                                    total: isSuccess(computeFeatureAccess)
                                        ? computeFeatureAccess.data.remaining
                                        : 0,
                                    used: isSuccess(computeFeatureAccess)
                                        ? computeFeatureAccess.data
                                              .currentQuantity
                                        : 0,
                                    unit: "credits",
                                }}
                            />
                        </Grid>
                        {isSuccess(freeComputeUnitsRd) &&
                            freeComputeUnitsRd.data
                                .fetchOrganizationFreeComputeUnitsRemaining
                                .unitsRemaining > 0 && (
                                <Stack
                                    height="100%"
                                    justifyContent="space-between"
                                    gap={1}
                                    minWidth="120px"
                                >
                                    <Stack
                                        sx={{
                                            padding: 2,
                                            border: 1,
                                            borderColor: (theme) =>
                                                theme.palette.stroke[100],
                                            borderRadius: (theme) =>
                                                theme.radius.default,
                                        }}
                                    >
                                        <Typography
                                            variant="body3"
                                            fontWeight="semibold"
                                        >
                                            {`${formatNumberWithPrecision(freeComputeUnitsRd.data.fetchOrganizationFreeComputeUnitsRemaining.unitsRemaining)} credits`}
                                        </Typography>
                                        <Typography
                                            variant="body3"
                                            fontWeight="semibold"
                                            color="disabled"
                                            colorSet="container"
                                            invertColors
                                        >
                                            Free trial credits
                                        </Typography>
                                    </Stack>
                                </Stack>
                            )}
                    </Stack>
                </Stack>
            </FlexScrollArea>
            <Stack width="100%" sx={{ padding: 4 }} gap={4}>
                <Tooltip
                    title={
                        !hasCredits
                            ? "You don't have enough credits to enable hot compute"
                            : ""
                    }
                    disableHover={hasCredits}
                >
                    <div>
                        <Button
                            onClick={handleConfirm}
                            fullWidth
                            disabled={disabled}
                        >
                            Enable Resource
                        </Button>
                    </div>
                </Tooltip>
                <Button fullWidth variant="outlined" onClick={closeModal}>
                    Nevermind
                </Button>
            </Stack>
        </FlexScrollAreaContainer>
    );
};
